/**
 * Decision Simulator — what-if scenarios with ranked decision cards
 * ====================================================================
 * The unification feature: turns CARTIE from diagnostic into
 * decision-support. Public, no-signup. Pre-loaded with 4 sample
 * scenarios so the page tells a story on first load.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import {
  ArrowLeft, Sparkles, ArrowRightLeft, ShieldCheck, Database, Boxes,
  UserMinus, Lock, Plus, Trash2, RefreshCcw, Zap, Loader2, Crown, Calculator,
} from 'lucide-react';
import { toast } from 'sonner';
import FAQSection from '../components/FAQSection';
import { faqsByPage } from '../data/faqs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '$0';
  const a = Math.abs(n); const sign = n < 0 ? '-' : (n > 0 ? '+' : '');
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${sign}$${(a / 1_000).toFixed(0)}K`;
  return `${sign}$${Math.round(a).toLocaleString()}`;
};

const SCENARIO_META = {
  swap_llm_model:         { icon: ArrowRightLeft, label: 'Swap LLM model',   accent: 'emerald' },
  increase_sp_coverage:   { icon: ShieldCheck,    label: 'Increase SP/RI coverage', accent: 'cyan' },
  add_caching_layer:      { icon: Database,       label: 'Add caching layer', accent: 'emerald' },
  rightsize_k8s_namespace:{ icon: Boxes,          label: 'Rightsize K8s namespace', accent: 'amber' },
  drop_customer:          { icon: UserMinus,      label: 'Drop unprofitable customer', accent: 'amber' },
  token_budget_cap:       { icon: Lock,           label: 'Cap token spend',  accent: 'cyan' },
};

const VERDICT_META = {
  high_value_low_risk: { label: 'High value · low risk', color: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  solid_save:          { label: 'Solid save',           color: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  marginal:            { label: 'Marginal',             color: 'bg-slate-100 text-slate-700 border-slate-300' },
  risky_save:          { label: 'Risky save',           color: 'bg-amber-50 text-amber-700 border-amber-300' },
  costly:              { label: 'Costs more',           color: 'bg-rose-50 text-rose-700 border-rose-300' },
  neutral:             { label: 'Inconclusive',         color: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const RISK_META = {
  low:    { dot: 'bg-emerald-500', label: 'low risk' },
  medium: { dot: 'bg-amber-500',   label: 'medium risk' },
  high:   { dot: 'bg-rose-500',    label: 'high risk' },
};

const REV_META = {
  instant:   'instant rollback',
  days:      'rollback in days',
  weeks:     'rollback in weeks',
  permanent: 'permanent commitment',
};

const STARTER_SCENARIOS = [
  { type: 'swap_llm_model', feature_slug: 'summarise',
    monthly_calls: 6_200, current_avg_cost_per_call_usd: 2.10,
    current_model: 'gpt-5.2', target_model: 'claude-haiku-4.5' },
  { type: 'increase_sp_coverage',
    monthly_compute_spend_usd: 113_000, current_coverage_pct: 38.9,
    target_coverage_pct: 65, provider: 'aws', term: '1y' },
  { type: 'add_caching_layer', feature_slug: 'search',
    current_monthly_cost_usd: 1_472, cache_hit_rate_pct: 70 },
  { type: 'rightsize_k8s_namespace', cluster_id: 'prod-eks',
    namespace: 'data-staging', current_monthly_cost_usd: 7_000,
    rightsize_savings_pct: 35 },
];

const SCENARIO_TEMPLATES = {
  swap_llm_model: { feature_slug: 'new-feature', monthly_calls: 1000,
    current_avg_cost_per_call_usd: 0.05, current_model: 'gpt-5.2',
    target_model: 'claude-haiku-4.5' },
  increase_sp_coverage: { monthly_compute_spend_usd: 50_000,
    current_coverage_pct: 30, target_coverage_pct: 60,
    provider: 'aws', term: '1y' },
  add_caching_layer: { feature_slug: 'feature', current_monthly_cost_usd: 1000,
    cache_hit_rate_pct: 70 },
  rightsize_k8s_namespace: { cluster_id: 'cluster-1', namespace: 'ns-a',
    current_monthly_cost_usd: 1000, rightsize_savings_pct: 35 },
  drop_customer: { customer_id: 'acme', customer_name: 'Acme Corp',
    monthly_revenue_usd: 5_000, monthly_cloud_cost_usd: 6_000 },
  token_budget_cap: { feature_slug: 'agent', current_monthly_cost_usd: 5_000,
    monthly_cap_usd: 3_000 },
};

const DecisionSimulator = () => {
  const [scenarios, setScenarios] = useState(STARTER_SCENARIOS);
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);

  const compute = useMemo(() => async () => {
    if (!scenarios.length) { setResult(null); return; }
    setComputing(true);
    try {
      const { data } = await axios.post(`${API}/decision-simulator/simulate`, { scenarios });
      setResult(data);
    } catch (e) {
      toast.error("Couldn't simulate", { description: e?.response?.data?.detail || e?.message });
    } finally { setComputing(false); }
  }, [scenarios]);

  useEffect(() => {
    const t = setTimeout(() => { compute(); }, 350);
    return () => clearTimeout(t);
  }, [compute]);

  const removeScenario = (i) => setScenarios((s) => s.filter((_, idx) => idx !== i));
  const updateScenario = (i, patch) =>
    setScenarios((s) => s.map((sc, idx) => idx === i ? { ...sc, ...patch } : sc));
  const addScenario = (type) => {
    const tpl = SCENARIO_TEMPLATES[type];
    if (!tpl) return;
    setScenarios((s) => [...s, { type, ...tpl }]);
  };

  const cards = result?.cards || [];

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="decision-simulator-page" data-tour="ds-scenarios">
      <SEOHead
        title="Decision Simulator — what-if your FinOps moves"
        description="Free what-if simulator for the 6 highest-leverage FinOps decisions. Model swap · SP coverage · caching · K8s rightsize · customer P&L · token caps. See ranked decision cards in 30 seconds."
        canonical="https://cartieai.com/decision-simulator"
        keywords="FinOps simulator, what-if AI cost, decision support cloud cost, model swap savings calculator"
      />

      {/* Top nav (matches Savings Calculator + Bill Score + AI Profitability) */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900 text-slate-100 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10" data-testid="back-link">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <header>
            <Badge className="mb-3 bg-emerald-50 text-emerald-700 border-emerald-200 tracking-wider">
              <Sparkles className="w-3 h-3 mr-1" /> FREE WHAT-IF SIMULATOR · NO SIGN-UP
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mb-3">
              See the impact{' '}
              <span className="text-emerald-600">
                before you make the call.
              </span>
            </h1>
            <p className="text-slate-600 max-w-3xl text-base sm:text-lg leading-relaxed">
              Six FinOps decision types, one simulator. Edit the inputs → live decision cards rank by impact, risk and reversibility. The math is shown — nothing hidden.
            </p>
          </header>

          {/* Top-line KPI strip */}
          {result && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpis">
              <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
                <CardContent className="p-4">
                  <p className="text-[11px] uppercase tracking-widest text-slate-600 mb-1">If you take all good moves</p>
                  <p className="text-2xl font-bold tabular-nums text-emerald-700">{fmtMoney(result.total_monthly_savings_if_all_taken)}/mo</p>
                  <p className="text-[11px] text-slate-600">≈ {fmtMoney(result.total_annual_savings_if_all_taken)}/yr</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-slate-200 shadow-sm"><CardContent className="p-4">
                <p className="text-[11px] uppercase tracking-widest text-slate-600 mb-1">Scenarios simulated</p>
                <p className="text-2xl font-bold tabular-nums text-slate-900">{cards.length}</p>
              </CardContent></Card>
              <Card className="bg-white border-slate-200 shadow-sm"><CardContent className="p-4">
                <p className="text-[11px] uppercase tracking-widest text-slate-600 mb-1">Top recommendation</p>
                <p className="text-sm font-medium text-emerald-700 line-clamp-2">{result.top_recommendation_summary || '—'}</p>
              </CardContent></Card>
              <Card className="bg-white border-slate-200 shadow-sm"><CardContent className="p-4">
                <p className="text-[11px] uppercase tracking-widest text-slate-600 mb-1">Saves identified</p>
                <p className="text-2xl font-bold tabular-nums text-slate-900">
                  {cards.filter((c) => c.monthly_impact_usd < 0).length}/{cards.length}
                </p>
              </CardContent></Card>
            </div>
          )}

          {/* Scenario inputs */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle className="text-slate-900 text-base flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-emerald-600" /> Scenarios
                </CardTitle>
                <CardDescription className="text-slate-600">Edit any field — math recomputes in 350ms.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={compute} disabled={computing}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="recompute-btn">
                  <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${computing ? 'animate-spin' : ''}`} /> Recompute
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {scenarios.map((s, i) => {
                const meta = SCENARIO_META[s.type] || { icon: Sparkles, label: s.type };
                const Icon = meta.icon;
                return (
                  <div key={i} className="p-3 rounded-md border border-slate-200 bg-slate-50" data-testid={`scenario-row-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-emerald-600" />
                      <span className="font-semibold text-sm text-slate-900">{meta.label}</span>
                      <Button onClick={() => removeScenario(i)} size="sm" variant="ghost"
                        className="ml-auto text-slate-500 hover:text-rose-600 hover:bg-rose-50 h-7 w-7 p-0"
                        data-testid={`remove-scenario-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {Object.entries(s).filter(([k]) => k !== 'type').map(([k, v]) => (
                        <div key={k}>
                          <label className="text-[11px] uppercase tracking-wider text-slate-600 block mb-0.5">{k.replace(/_/g, ' ')}</label>
                          <Input
                            value={v ?? ''}
                            onChange={(e) => {
                              const num = Number(e.target.value);
                              const isNum = typeof v === 'number' || (!Number.isNaN(num) && e.target.value !== '' && /^[\d.\-]+$/.test(e.target.value));
                              updateScenario(i, { [k]: isNum ? num : e.target.value });
                            }}
                            className="bg-white border-slate-300 text-slate-900 h-8 text-xs font-mono"
                            data-testid={`scenario-${i}-${k}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Add new scenario */}
              <div className="border-t border-slate-200 pt-3">
                <p className="text-[11px] uppercase tracking-wider text-slate-600 mb-2">Add a scenario</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(SCENARIO_META).map(([key, meta]) => {
                    const Icon = meta.icon;
                    return (
                      <Button key={key} onClick={() => addScenario(key)} size="sm" variant="outline"
                        className="border-slate-300 text-slate-700 hover:border-emerald-500/60 hover:text-emerald-700 hover:bg-emerald-50"
                        data-testid={`add-scenario-${key}`}>
                        <Icon className="w-3.5 h-3.5 mr-1.5" /> {meta.label}
                        <Plus className="w-3 h-3 ml-1.5" />
                      </Button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Decision Cards */}
          {cards.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-emerald-600" /> Decision cards · ranked
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="decision-cards">
                {cards.map((c, i) => {
                  const meta = SCENARIO_META[c.type] || { icon: Sparkles };
                  const Icon = meta.icon;
                  const verdict = VERDICT_META[c.verdict] || VERDICT_META.neutral;
                  const risk = RISK_META[c.risk] || RISK_META.medium;
                  const isTop = c.is_top_recommendation;
                  return (
                    <Card
                      key={i}
                      className={`relative shadow-sm ${isTop ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white'}`}
                      data-testid={`decision-card-${i}`}>
                      {isTop && (
                        <Badge className="absolute -top-2 right-3 bg-emerald-500 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white border-0 text-[11px] tracking-widest font-bold">
                          <Crown className="w-3 h-3 mr-1" /> TOP MOVE
                        </Badge>
                      )}
                      <CardContent className="p-5 space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-emerald-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-900 text-sm leading-tight">{c.summary}</p>
                            <Badge variant="outline" className={`mt-1.5 text-[11px] ${verdict.color}`}>
                              {verdict.label}
                            </Badge>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-2">
                          <div>
                            <p className="text-[11px] uppercase tracking-widest text-slate-600">Monthly impact</p>
                            <p className={`text-2xl font-bold tabular-nums font-mono ${c.monthly_impact_usd < 0 ? 'text-emerald-700' : c.monthly_impact_usd > 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                              {fmtMoney(c.monthly_impact_usd)}<span className="text-xs text-slate-500 font-normal">/mo</span>
                            </p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase tracking-widest text-slate-600">Annual impact</p>
                            <p className={`text-2xl font-bold tabular-nums font-mono ${c.annual_impact_usd < 0 ? 'text-emerald-700' : c.annual_impact_usd > 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                              {fmtMoney(c.annual_impact_usd)}<span className="text-xs text-slate-500 font-normal">/yr</span>
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 text-[11px] text-slate-600 flex-wrap">
                          <span className="inline-flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${risk.dot}`}></span>{risk.label}
                          </span>
                          <span>·</span>
                          <span>{REV_META[c.reversibility] || c.reversibility}</span>
                          <span>·</span>
                          <span>~{c.effort_engineer_hours}h work</span>
                          <span>·</span>
                          <span>{c.time_to_value}</span>
                        </div>

                        {c.narrative && (
                          <p className="text-xs text-slate-700 leading-relaxed border-t border-slate-200 pt-3">
                            {c.narrative}
                          </p>
                        )}

                        {c.side_effects?.length > 0 && (
                          <div className="space-y-0.5">
                            <p className="text-[11px] uppercase tracking-widest text-slate-600 mb-1">Side effects</p>
                            {c.side_effects.map((se, j) => (
                              <p key={j} className="text-[11px] text-slate-600">· {se}</p>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA */}
          <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 via-white to-cyan-50 border-emerald-200 shadow-sm">
            <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-900 mb-1">Run this against your real numbers</h3>
                <p className="text-sm text-slate-700">
                  Sign in and the simulator hydrates from your AI features, K8s clusters and Customer P&L automatically — no retyping.
                </p>
              </div>
              <Link to="/login"><Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold transition-all font-semibold shadow-md" data-testid="cta-signin">
                Run on my data →
              </Button></Link>
            </CardContent>
          </Card>

          {computing && (
            <p className="text-center text-xs text-slate-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> recomputing…</p>
          )}

          <FAQSection
            title="Decision Simulator FAQ"
            items={faqsByPage.decision_simulator}
            testIdPrefix="ds-faq"
            variant="light"
          />
        </div>
      </main>
    </div>
  );
};

export default DecisionSimulator;
